import type { Dispatch, SetStateAction } from "react";
import { Badge } from "./Badge";
import { amazonDeliveryStatus } from "../lib/amazonStatus";
import { amazonAdProductLabel, yen } from "../lib/meta";
import type { AmazonAdProduct, Campaign } from "../types/contract";

/**
 * Amazon タブ専用の一覧表（2026-10-30・設計 docs/ad-console-amazon-sbsd-ui-design-2026-10-30.md §2）。
 * 共通18列のうち Amazon では常に空の「チャネル/主KPI単価/楽天購入/楽天購入額」を落とし、
 * 注文・購入CPA・売上・ACOS・imp を列に昇格させた15列。サブフィルタ「すべて」のときは SP→SB→SD の
 * 見出し行＋小計行を挟む。操作系（予算編集・一時停止/再開・ターゲット展開）のハンドラは親から受け取る。
 */
const COL_COUNT = 15;

/** グループの表示順。未知の ad_product は末尾の「その他」にまとめる（隠さない）。 */
const PRODUCT_ORDER: AmazonAdProduct[] = ["SPONSORED_PRODUCTS", "SPONSORED_BRANDS", "SPONSORED_DISPLAY"];

export type BudgetEditState = { id: string; value: string } | null;

export interface AmazonCampaignsTableProps {
  /** 表示対象の Amazon 行（チャネル/検索/サブフィルタ適用済み・API の並びを維持） */
  rows: Campaign[];
  /** true: SP/SB/SD ごとに見出し行＋小計行を出す（サブフィルタ「すべて」のとき） */
  grouped: boolean;
  budgetEdit: BudgetEditState;
  setBudgetEdit: Dispatch<SetStateAction<BudgetEditState>>;
  budgetError: string | null;
  setBudgetError: Dispatch<SetStateAction<string | null>>;
  submitBudgetEdit: (campaign: Campaign) => void;
  openConfirm: (campaign: Campaign, action: "pause" | "activate") => void;
  toggleAds: (campaign: Campaign) => void;
  expandedId: string | null;
}

interface Totals {
  budget: number;
  spend: number;
  imp: number;
  clicks: number;
  orders: number;
  sales: number;
}

function sumRows(rows: Campaign[]): Totals {
  const t: Totals = { budget: 0, spend: 0, imp: 0, clicks: 0, orders: 0, sales: 0 };
  for (const c of rows) {
    // 終了（ARCHIVED）は日予算に数えない（配信されないため）。実績は期間内なので含める。
    if (c.amazon?.state !== "ARCHIVED") t.budget += c.daily_budget ?? 0;
    t.spend += c.spend;
    t.imp += c.impressions;
    t.clicks += c.clicks;
    t.orders += c.purchases;
    t.sales += c.purchase_value;
  }
  return t;
}

const DASH = "—";
const pct1 = (n: number) => `${n.toFixed(1)}%`;

export function AmazonCampaignsTable(props: AmazonCampaignsTableProps) {
  const { rows, grouped } = props;

  const groups = (() => {
    const known = PRODUCT_ORDER.map((p) => ({ key: p as string, rows: rows.filter((c) => c.amazon?.ad_product === p) }));
    const other = rows.filter((c) => !PRODUCT_ORDER.includes(c.amazon?.ad_product as AmazonAdProduct));
    const list = [...known, ...(other.length ? [{ key: "OTHER", rows: other }] : [])];
    return list.filter((g) => g.rows.length > 0);
  })();

  return (
    <div className="table-wrap">
      <table className="data-table amz-table">
        <thead>
          <tr>
            <th>キャンペーン名</th>
            <th>配信状態</th>
            <th>日予算</th>
            <th>広告費</th>
            <th>imp</th>
            <th>clicks</th>
            <th>CTR</th>
            <th>CPC</th>
            <th>注文</th>
            <th>購入CPA</th>
            <th>売上</th>
            <th>ACOS</th>
            <th>ROAS</th>
            <th>操作</th>
            <th>詳細</th>
          </tr>
        </thead>
        <tbody>
          {grouped
            ? groups.map((g) => {
                const label = g.key === "OTHER" ? { short: "他", long: "その他" } : amazonAdProductLabel(g.key);
                return (
                  <GroupBlock
                    key={g.key}
                    {...props}
                    shortLabel={label.short}
                    longLabel={label.long}
                    groupRows={g.rows}
                    totals={sumRows(g.rows)}
                  />
                );
              })
            : rows.map((c) => <CampaignRow key={c.id} c={c} {...props} />)}
        </tbody>
      </table>
    </div>
  );
}

function GroupBlock(
  props: AmazonCampaignsTableProps & { shortLabel: string; longLabel: string; groupRows: Campaign[]; totals: Totals }
) {
  const { shortLabel, longLabel, groupRows, totals: t } = props;
  return (
    <>
      <tr className="amz-group-row">
        <td colSpan={COL_COUNT}>
          {/* ラベルだけ sticky にして、横スクロールしても見出しが左端に残るようにする */}
          <div className="amz-group-label">
            <strong>{shortLabel}</strong> {longLabel}{" "}
            <span className="amz-group-meta">
              {groupRows.length}本 ・ 日予算 {yen(t.budget)}
            </span>
          </div>
        </td>
      </tr>
      {groupRows.map((c) => (
        <CampaignRow key={c.id} c={c} {...props} />
      ))}
      <tr className="amz-total-row">
        <td>{shortLabel} 小計</td>
        <td />
        <td>{yen(t.budget)}</td>
        <td>{yen(t.spend)}</td>
        <td>{t.imp.toLocaleString("ja-JP")}</td>
        <td>{t.clicks.toLocaleString("ja-JP")}</td>
        <td>{t.imp > 0 ? `${((t.clicks / t.imp) * 100).toFixed(2)}%` : DASH}</td>
        <td>{t.clicks > 0 ? yen(t.spend / t.clicks) : DASH}</td>
        <td>{t.orders.toLocaleString("ja-JP")}件</td>
        <td>{t.orders > 0 ? yen(t.spend / t.orders) : DASH}</td>
        <td>{t.sales > 0 ? yen(t.sales) : DASH}</td>
        <td>{t.sales > 0 ? pct1((t.spend / t.sales) * 100) : DASH}</td>
        <td>{t.spend > 0 && t.sales > 0 ? `${(t.sales / t.spend).toFixed(2)}x` : DASH}</td>
        <td />
        <td />
      </tr>
    </>
  );
}

function CampaignRow(props: AmazonCampaignsTableProps & { c: Campaign }) {
  const { c, budgetEdit, setBudgetEdit, budgetError, setBudgetError, submitBudgetEdit, openConfirm, toggleAds, expandedId } = props;
  const amz = c.amazon;
  const product = amz ? amazonAdProductLabel(amz.ad_product) : null;
  const delivery = amazonDeliveryStatus(c);
  const isActive = c.status === "ACTIVE";
  const canEditBudget = c.operable;
  return (
    <tr>
      <td style={{ whiteSpace: "normal", minWidth: 220 }}>
        {c.name}
        {amz && product && (
          <span className="badge-stack amz-badges">
            <span title={product.long}>
              <Badge label={product.short} color="var(--pf-amazon-c)" bg="var(--pf-amazon-bg)" />
            </span>
            {amz.targeting_type && <Badge label={amz.targeting_type} color="var(--ink-sub)" bg="var(--ch-all-bg)" />}
          </span>
        )}
      </td>
      <td>
        <Badge label={delivery.label} color={delivery.c} bg={delivery.bg} />
        {delivery.sub && <span className="cell-sub">{delivery.sub}</span>}
      </td>
      <td>
        {canEditBudget && budgetEdit?.id === c.id ? (
          <span className="budget-edit">
            <input
              type="text"
              inputMode="numeric"
              value={budgetEdit.value}
              aria-label="日予算（円）"
              onChange={(e) => setBudgetEdit({ id: c.id, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitBudgetEdit(c);
                if (e.key === "Escape") setBudgetEdit(null);
              }}
              autoFocus
            />
            <button className="btn btn-sm" onClick={() => submitBudgetEdit(c)}>
              保存
            </button>
            <button
              className="link-btn"
              onClick={() => {
                setBudgetEdit(null);
                setBudgetError(null);
              }}
            >
              取消
            </button>
            {budgetError && <span className="ad-card-copy-failed">{budgetError}</span>}
          </span>
        ) : (
          <span className="budget-cell">
            {c.daily_budget != null ? yen(c.daily_budget) : DASH}
            {amz?.budget_usage_pct != null && <span className="cell-sub">当日消化 {Math.round(amz.budget_usage_pct)}%</span>}
            {canEditBudget && (
              <button
                className="link-btn"
                onClick={() => {
                  setBudgetError(null);
                  setBudgetEdit({ id: c.id, value: String(c.daily_budget ?? "") });
                }}
                title="日予算を変更（Amazon広告のキャンペーン予算を書き換えます）"
              >
                変更
              </button>
            )}
          </span>
        )}
      </td>
      <td>{yen(c.spend)}</td>
      <td>{c.impressions.toLocaleString("ja-JP")}</td>
      <td>{c.clicks.toLocaleString("ja-JP")}</td>
      <td>{c.ctr}</td>
      <td>{c.cpc != null ? yen(c.cpc) : DASH}</td>
      <td>{c.purchases.toLocaleString("ja-JP")}件</td>
      <td>{c.cpa_purchase != null ? yen(c.cpa_purchase) : DASH}</td>
      <td>{c.purchase_value > 0 ? yen(c.purchase_value) : DASH}</td>
      <td>{amz?.acos != null ? pct1(amz.acos) : DASH}</td>
      <td>{c.roas != null ? `${c.roas.toFixed(2)}x` : DASH}</td>
      <td>
        {c.operable ? (
          <button
            className="btn btn-sm"
            onClick={() => openConfirm(c, isActive ? "pause" : "activate")}
            title="キャンペーン本体の配信ステータスを変更します"
          >
            {isActive ? "一時停止" : "再開"}
          </button>
        ) : (
          <span style={{ color: "var(--ink-faint)", fontSize: 11.5 }}>—</span>
        )}
      </td>
      <td>
        <button className="btn btn-sm" onClick={() => toggleAds(c)}>
          {expandedId === c.id ? "ターゲットを閉じる" : "ターゲットを見る"}
        </button>
      </td>
    </tr>
  );
}
